/**
 * Component Name: FilterModal
 * Description: A modal for filtering invoices by supplier, status, date range, and amount, with custom start and end date inputs for the "Custom" date range.
 * Props:
 *   - isFilterModalOpen (boolean): Toggles modal visibility.
 *   - handleCloseFilterModal (function): Closes the modal without applying filters.
 *   - handleApplyFiltersAndCloseModal (function): Applies selected filters and closes the modal.
 *   - filterState (object): Current filter state with supplierId, status, dateRange, minAmount, maxAmount, startDate, and endDate.
 * Feature: Allows users to filter invoices with sorted supplier options, a date range selector (including a custom range with start and end dates), and amount inputs, with temporary state management for applying filters.
 */
import React, { useState, useEffect } from 'react';
import moment from 'moment';
import Modal from '../modal/modal';
import MultiSelect from '../inputs/multi-select';
import NumberInput from '../inputs/new-input';
import DatePickerInput from '../inputs/date-picker';
import _ from 'lodash';
import { ValidateField, ValidatorTypes } from '../../util/validators';
import { INVOICE_DATE_FORMAT, INVOICE_FILTER_OPTIONS, INVOICE_UI_LABELS } from '../../util/enums/invoice-management';

const FilterModal = ({
    isFilterModalOpen,
    handleCloseFilterModal,
    handleApplyFiltersAndCloseModal,
    filterState,
}) => {
    // Error handling state
    const [error, setError] = useState(null);

    // Initialize temporary filter state with startDate and endDate
    const [tempFilterState, setTempFilterState] = useState({
        ...filterState,
        startDate: { value: filterState.startDate?.value || '' },
        endDate: { value: filterState.endDate?.value || '' },
    });
    const [customDateRangeFields, setCustomDateRangeFields] = useState({
        startDate: {
            value: '',
            label: 'Start Date',
            placeholder: 'Select Start Date',
            error: false,
            valid: true,
            clearable: true,
            disabled: false,
            name: 'startDate',
            className: "filter-date-picker",
            validation: [ValidatorTypes.REQUIRED]
        },
        endDate: {
            value: '',
            label: 'End Date',
            placeholder: 'Select End Date',
            error: false,
            valid: true,
            clearable: true,
            name: 'endDate',
            className: "filter-date-picker",
            disabled: false,
            validation: [ValidatorTypes.REQUIRED]
        }
    });

    // Syncs tempFilterState with filterState when the modal reopens.
    useEffect(() => {
        setTempFilterState({
            ...filterState,
            startDate: { ...customDateRangeFields.startDate },
            endDate: { ...customDateRangeFields.endDate },
        });
    }, [filterState]);

    // Updates temporary filter state with selected values, formatting dates as DD/MM/YYYY.
    const handleTempFilterChange = (selectedOption, actionMeta) => {
        try {
            const name = actionMeta ? actionMeta.name : selectedOption.target?.name || selectedOption.name;
            let value = actionMeta ? selectedOption : selectedOption.target?.value;
            // Format date fields
            if (name === 'startDate' || name === 'endDate') {
                value = value && moment(value).isValid() ? moment(value).format(INVOICE_DATE_FORMAT) : '';
            }
            setTempFilterState((prevState) => ({
                ...prevState,
                [name]: {
                    ...prevState[name],
                    value,
                },
            }));

            setCustomDateRangeFields((prev) => ({
                ...prev,
                [name]: {
                    ...prev[name],
                    value,
                },
            }));
        } catch (error) {
            console.error('Error handling filter change:', error);
            setError('Failed to update filter. Please try again.');
        }
    };

    // Clear all filters and reset to default state
    const handleClearFilters = () => {
        const clearedFilterState = {
            searchTerm: {
                ...filterState.searchTerm,
                value: ''
            },
            supplierId: {
                ...filterState.supplierId,
                value: { label: INVOICE_FILTER_OPTIONS.ALL_SUPPLIERS, value: INVOICE_FILTER_OPTIONS.ALL_SUPPLIERS }
            },
            status: {
                ...filterState.status,
                value: { label: INVOICE_FILTER_OPTIONS.ALL_PAYMENT_STATUS, value: INVOICE_FILTER_OPTIONS.ALL_PAYMENT_STATUS }
            },
            dateRange: {
                ...filterState.dateRange,
                value: { label: INVOICE_FILTER_OPTIONS.ALL_TIME, value: INVOICE_FILTER_OPTIONS.ALL_TIME }
            },
            minAmount: {
                ...filterState.minAmount,
                value: ''
            },
            maxAmount: {
                ...filterState.maxAmount,
                value: ''
            },
            startDate: {
                ...filterState.startDate,
                value: ''
            },
            endDate: {
                ...filterState.endDate,
                value: ''
            },
            selectedProjectId: {
                ...filterState.selectedProjectId,
                value: null
            },
            selectedStageId: {
                ...filterState.selectedStageId,
                value: null
            },
            selectedTaskId: {
                ...filterState.selectedTaskId,
                value: null
            }
        };

        setTempFilterState(clearedFilterState);
        setCustomDateRangeFields({
            startDate: {
                ...customDateRangeFields.startDate,
                value: ''
            },
            endDate: {
                ...customDateRangeFields.endDate,
                value: ''
            }
        });
    };

    // Applies the temporary filter state and closes the modal.
    const handleApply = () => {
        if (tempFilterState.dateRange.value.value === INVOICE_FILTER_OPTIONS.CUSTOM) {
            for (const key in customDateRangeFields) {
                const field = customDateRangeFields[key];
                const { valid, error } = ValidateField({ value: field.value, validation: field.validation });
                field.error = error;
                field.valid = valid;
                if (!valid) {
                    setCustomDateRangeFields((prev) => ({
                        ...prev,
                        [key]: field,
                    }));
                }
            }
            if (customDateRangeFields.startDate.valid && customDateRangeFields.endDate.valid) {
                handleApplyFiltersAndCloseModal(tempFilterState);
            }
        } else {
            handleApplyFiltersAndCloseModal(tempFilterState);
        }
    };

    // Sorts supplier options alphabetically, keeping "All Suppliers" at the top.
    const getSupplierOptions = (suppliers) => {
        const allSuppliersOption = suppliers.find(supplier => supplier.value === INVOICE_FILTER_OPTIONS.ALL_SUPPLIERS);
        const otherSuppliers = suppliers.filter(supplier => supplier.value !== INVOICE_FILTER_OPTIONS.ALL_SUPPLIERS);
        const sortedOtherSuppliers = _.orderBy(otherSuppliers, [supplier => supplier.label.toLowerCase()], ['asc']);
        return allSuppliersOption ? [allSuppliersOption, ...sortedOtherSuppliers] : sortedOtherSuppliers;
    };
    // Error display component
    const ErrorDisplay = ({ error }) => (
        <div className="alert alert-danger mb-3" role="alert">
            <strong>Error:</strong> {error}
        </div>
    );

    return (
        <Modal
            className="filter-modal"
            modalSaveTxt={INVOICE_UI_LABELS.APPLY_FILTERS}
            open={isFilterModalOpen}
            onClose={handleCloseFilterModal}
            modalCloseTxt={INVOICE_UI_LABELS.CLOSE}
            title={INVOICE_UI_LABELS.FILTER_INVOICES}
            maxWidth="sm"
            modalHeading={INVOICE_UI_LABELS.ADD_FILTERS}
            onClick={handleApply}
            showActionBtn
            savebtnClass="filter-btn"
            primary
            dataCy="invoice-management-filter-modal"
            additionalbtnTxt={INVOICE_UI_LABELS.CLEAR_FILTERS}
            additionalbtn={handleClearFilters}
        >
            {error && <ErrorDisplay error={error} />}

            <div className="modal-body">
                <div className="d-flex flex-column flex-wrap gap-3">
                    <MultiSelect
                        className="filter-select"
                        value={tempFilterState.supplierId.value}
                        onChangeHandler={(selectedOption) => handleTempFilterChange(selectedOption, { name: 'supplierId' })}
                        isMulti={false}
                        label={tempFilterState.supplierId.label}
                        placeholder={tempFilterState.supplierId.label}
                        items={getSupplierOptions(tempFilterState.supplierId.options)}
                    />

                    <MultiSelect
                        className="filter-select"
                        value={tempFilterState.status.value}
                        onChangeHandler={(selectedOption) => handleTempFilterChange(selectedOption, { name: 'status' })}
                        isMulti={false}
                        label={tempFilterState.status.label}
                        placeholder={tempFilterState.status.label}
                        items={_.orderBy(tempFilterState.status.options, ['label'], ['asc'])}
                    />

                    <MultiSelect
                        className="filter-select"
                        value={tempFilterState.dateRange.value}
                        onChangeHandler={(selectedOption) => handleTempFilterChange(selectedOption, { name: 'dateRange' })}
                        isMulti={false}
                        label={tempFilterState.dateRange.label}
                        placeholder={tempFilterState.dateRange.label}
                        items={_.orderBy(tempFilterState.dateRange.options, ['label'], ['asc'])}
                    />

                    {tempFilterState.dateRange.value.value === 'Custom' && (
                        <>
                            <DatePickerInput
                                {...customDateRangeFields.startDate}
                                onChange={(val) => handleTempFilterChange(val, { name: 'startDate', value: val })}
                                popperProps={{
                                    anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                                    transformOrigin: { vertical: 'top', horizontal: 'left' },
                                    sx: { zIndex: 9999 }
                                }}
                            />
                            <DatePickerInput
                                {...customDateRangeFields.endDate}
                                onChange={(val) => handleTempFilterChange(val, { name: 'endDate', value: val })}
                                popperProps={{
                                    anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                                    transformOrigin: { vertical: 'top', horizontal: 'left' },
                                    sx: { zIndex: 9999 }
                                }}
                            />
                        </>
                    )}

                    <NumberInput
                        className="filter-input"
                        placeholder={tempFilterState.minAmount.placeholder}
                        {...tempFilterState.minAmount}
                        onChange={handleTempFilterChange}
                        name="minAmount"
                        customInputClass='w-97'
                    />

                    <NumberInput
                        className="filter-input"
                        placeholder={tempFilterState.maxAmount.placeholder}
                        {...tempFilterState.maxAmount}
                        onChange={handleTempFilterChange}
                        name="maxAmount"
                        customInputClass='w-97'
                    />
                </div>
            </div>
        </Modal>
    );
};

export default FilterModal;